import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { FormEvent, MouseEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRequest } from '../../hooks/useRequest';
import {
  createSkill,
  deleteSkill,
  fetchSkills,
  Skill,
  updateSkill,
} from '../../utils/axios/api';

export function AdminSkillsPage() {
  const { t } = useTranslation();
  const loadRequest = useRequest<Skill[]>();
  const saveRequest = useRequest<Skill>();
  const deleteRequest = useRequest();
  const [skills, setSkills] = useState<Skill[]>([]);

  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState<Skill | null>(null);
  const [name, setName] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [description, setDescription] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const [skillToDelete, setSkillToDelete] = useState<Skill | null>(null);

  useEffect(() => {
    void loadRequest.fetch(() => fetchSkills()).then((data) => {
      if (!data) return;
      setSkills(data);
    });
  }, [loadRequest]);

  const resetFormDialog = useCallback(() => {
    setEditingSkill(null);
    setName('');
    setShortDescription('');
    setDescription('');
    setValidationError(null);
  }, []);

  const handleOpenCreateDialog = useCallback(() => {
    resetFormDialog();
    setIsFormDialogOpen(true);
  }, [resetFormDialog]);

  const handleOpenEditDialog = useCallback((skill: Skill) => {
    setEditingSkill(skill);
    setName(skill.name);
    setShortDescription(skill.shortDescription);
    setDescription(skill.description);
    setValidationError(null);
    setIsFormDialogOpen(true);
  }, []);

  const handleCloseFormDialog = useCallback(() => {
    if (saveRequest.isLoading) {
      return;
    }

    setIsFormDialogOpen(false);
    resetFormDialog();
  }, [resetFormDialog, saveRequest.isLoading]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const trimmedName = name.trim();
      const trimmedShortDescription = shortDescription.trim();
      const trimmedDescription = description.trim();

      if (!trimmedName || !trimmedShortDescription || !trimmedDescription) {
        setValidationError(t('admin.skills.errors.required'));
        return;
      }

      setValidationError(null);

      const payload = {
        name: trimmedName,
        shortDescription: trimmedShortDescription,
        description: trimmedDescription,
      };

      if (editingSkill) {
        const updated = await saveRequest.fetch(() =>
          updateSkill(editingSkill.id, payload),
        );
        if (!updated) {
          return;
        }

        setSkills((prev) =>
          prev.map((item) => (item.id === updated.id ? updated : item)),
        );
      } else {
        const created = await saveRequest.fetch(() => createSkill(payload));
        if (!created) {
          return;
        }

        setSkills((prev) => [created, ...prev]);
      }

      setIsFormDialogOpen(false);
      resetFormDialog();
    },
    [
      description,
      editingSkill,
      name,
      resetFormDialog,
      saveRequest,
      shortDescription,
      t,
    ],
  );

  const handleOpenDeleteDialog = useCallback(
    (event: MouseEvent, skill: Skill) => {
      event.stopPropagation();
      event.preventDefault();
      setSkillToDelete(skill);
    },
    [],
  );

  const handleCloseDeleteDialog = useCallback(() => {
    if (deleteRequest.isLoading) {
      return;
    }

    setSkillToDelete(null);
  }, [deleteRequest.isLoading]);

  const handleDelete = useCallback(async () => {
    if (!skillToDelete) {
      return;
    }

    const result = await deleteRequest.fetch(async () => {
      await deleteSkill(skillToDelete.id);
    });
    if (result === null) {
      return;
    }
    setSkills((prev) => prev.filter((item) => item.id !== skillToDelete.id));
    setSkillToDelete(null);
  }, [deleteRequest, skillToDelete]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        p: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Typography variant="subtitle1">
          {t('admin.skills.description')}
        </Typography>
        <Button variant="contained" onClick={handleOpenCreateDialog}>
          {t('admin.skills.create.button')}
        </Button>
      </Box>

      {loadRequest.isLoading ? (
        <CircularProgress size={24} />
      ) : loadRequest.exception && skills.length === 0 ? (
        <Typography variant="body2" color="error">
          {t(`exceptions.${loadRequest.exception}`)}
        </Typography>
      ) : skills.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('admin.skills.empty')}
        </Typography>
      ) : (
        skills.map((skill) => (
          <Box
            key={skill.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              border: 1,
              borderColor: 'divider',
              borderRadius: '10px',
              bgcolor: 'background.paper',
              '&:hover': {
                borderColor: 'text.secondary',
                bgcolor: 'action.hover',
              },
            }}
          >
            <ButtonBase
              onClick={() => {
                handleOpenEditDialog(skill);
              }}
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                px: 1.5,
                py: 1.25,
                borderRadius: '10px',
                textAlign: 'left',
                color: 'inherit',
              }}
            >
              <Typography variant="body1" noWrap sx={{ width: '100%' }}>
                {skill.name}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                sx={{ width: '100%' }}
              >
                {skill.shortDescription}
              </Typography>
            </ButtonBase>
            <IconButton
              aria-label={t('admin.skills.delete.button')}
              color="error"
              onClick={(event) => {
                handleOpenDeleteDialog(event, skill);
              }}
              sx={{ mr: 0.5 }}
            >
              <DeleteOutlinedIcon />
            </IconButton>
          </Box>
        ))
      )}

      <Dialog
        open={isFormDialogOpen}
        onClose={handleCloseFormDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingSkill
            ? t('admin.skills.edit.title')
            : t('admin.skills.create.title')}
        </DialogTitle>
        <Box
          component="form"
          onSubmit={(event) => {
            void handleSubmit(event);
          }}
        >
          <DialogContent
            sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
          >
            <TextField
              label={t('admin.skills.name')}
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
              disabled={saveRequest.isLoading}
              autoFocus
              required
              fullWidth
            />
            <TextField
              label={t('admin.skills.shortDescription')}
              value={shortDescription}
              onChange={(event) => {
                setShortDescription(event.target.value);
              }}
              disabled={saveRequest.isLoading}
              required
              fullWidth
            />
            <TextField
              label={t('admin.skills.fullDescription')}
              value={description}
              onChange={(event) => {
                setDescription(event.target.value);
              }}
              disabled={saveRequest.isLoading}
              required
              fullWidth
              multiline
              minRows={4}
            />
            {validationError ?? saveRequest.exception ? (
              <Typography variant="body2" color="error">
                {validationError ?? t(`exceptions.${saveRequest.exception}`)}
              </Typography>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseFormDialog}
              disabled={saveRequest.isLoading}
            >
              {t('admin.skills.create.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saveRequest.isLoading}
            >
              {saveRequest.isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : editingSkill ? (
                t('admin.skills.save')
              ) : (
                t('admin.skills.create.submit')
              )}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(skillToDelete)} onClose={handleCloseDeleteDialog}>
        <DialogTitle>{t('admin.skills.delete.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('admin.skills.delete.confirm', {
              name: skillToDelete?.name ?? '',
            })}
          </DialogContentText>
          {deleteRequest.exception ? (
            <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
              {t(`exceptions.${deleteRequest.exception}`)}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteDialog}
            disabled={deleteRequest.isLoading}
          >
            {t('admin.skills.delete.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteRequest.isLoading}
            onClick={() => {
              void handleDelete();
            }}
          >
            {deleteRequest.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('admin.skills.delete.button')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
